import React, { useEffect, useRef, useCallback } from "react";
import { IEmbeddedKrakenV2Props } from "./types";

const EmbeddedKrakenV2: React.FC<IEmbeddedKrakenV2Props> = ({
  id,
  paymentSession,
  className,
  onSuccess,
  btnLocale,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const getConfig = useCallback(
    () => ({
      sessionToken: paymentSession.token,
      amount: paymentSession.request.amount,
      currency: paymentSession.request.currency,
      userId: paymentSession.request.userId,
      clientReferenceId: paymentSession.request.txId,
      supportApplePay: true,
      supportGooglePay: true,
      supportCreditCard: false,
      paymentPublishableKey: process.env.REACT_APP_PAYMENT_PUBLISHABLE_KEY,
      btnLocale,
    }),
    [paymentSession, btnLocale],
  );

  const getDimensions = useCallback(() => {
    const iframe = iframeRef.current;
    return {
      width: iframe?.offsetWidth,
      height: iframe?.offsetHeight,
    };
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;

    const handlePaymentSuccess = async (event: MessageEvent) => {
      if (event.data.type === "PAYMENT_SUCCESS") {
        await onSuccess();
        window.removeEventListener("message", handlePaymentSuccess);
      }
    };

    const handlePageReady = (event: MessageEvent) => {
      if (event.data.type === "PAYMENT_READY") {
        iframe?.contentWindow?.postMessage(
          {
            type: "PAYMENT_CONFIG",
            config: getConfig(),
            dimensions: getDimensions(),
          },
          "*",
        );
        window.removeEventListener("message", handlePageReady);
        window.addEventListener("message", handlePaymentSuccess);
      }
    };

    window.addEventListener("message", handlePageReady);

    return () => {
      window.removeEventListener("message", handlePageReady);
      window.removeEventListener("message", handlePaymentSuccess);
    };
  }, [getConfig, getDimensions, onSuccess]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    iframe.contentWindow?.postMessage(
      {
        type: "PAYMENT_CONFIG",
        config: getConfig(),
        dimensions: getDimensions(),
      },
      "*",
    );
  }, [getConfig, getDimensions]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data &&
        event.data.type === "IFRAME_HEIGHT" &&
        event.data.height
      ) {
        if (!id) return;
        const iframe = document.getElementById(id);
        if (iframe) {
          (iframe as HTMLIFrameElement).style.height = event.data.height + "px";
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [id]);

  return (
    <iframe
      id={id}
      ref={iframeRef}
      src={`${process.env.REACT_APP_WEB_SECURE}/embed/trip-payment`}
      title="Payment Gateway"
      className={className}
      style={{
        width: "100%",
        border: "none",
        display: "block",
        overflow: "visible",
      }}
      scrolling="no"
      allow="payment"
    />
  );
};

export default EmbeddedKrakenV2;
